/**
 * Why a tenant's dashboard analysis is missing.
 *
 * On a control plane these calls are forwarded to the tenant's own container.
 * A tenant that has not been moved yet has no container to forward to, and the
 * forwarder answers 502 — an expected state for the whole tenant-by-tenant
 * rollout, not a fault. The dashboard has to name it, because dropping the
 * tenant from the results left its cells rendering "Loading…" forever,
 * indistinguishable from a slow query.
 */
export type AnalysisFailure = 'unreachable' | 'error';

/**
 * The HTTP status behind a failed request, whatever shape the error arrived in.
 *
 * The analysis services catch the axios error, log its status, and then
 * `throw new Error('Failed to get order analysis')` — so by the time the caller
 * sees it the status is gone. `withStatus` re-attaches it; this reads either
 * shape, so a service that has not been converted still works.
 */
function statusOf(error: unknown): number | undefined {
  const e = error as { status?: number; response?: { status?: number } };
  return e?.status ?? e?.response?.status;
}

/**
 * Classify a failed analysis fetch.
 *
 * Both "not served by a container" answers come from the forwarder and mean the
 * same thing to a reader of this table — there is nowhere to ask:
 *   502  the tenant has a status document but no registered container_url
 *        (not moved yet, or its container is down)
 *   404  the forwarder does not know this tenant at all (no status document —
 *        an agent has never run for it)
 * Anything else is a genuine fault and is shown as one.
 */
export function analysisFailureFrom(error: unknown): AnalysisFailure {
  const status = statusOf(error);
  return status === 502 || status === 404 ? 'unreachable' : 'error';
}

/**
 * Re-throwable error that keeps the HTTP status.
 *
 * Use in a service's catch block instead of a bare `new Error(...)`: the caller
 * cannot tell "this tenant has no container" (502) from a real fault without it.
 */
export function withStatus(message: string, error: unknown): Error & { status?: number } {
  const wrapped = new Error(message) as Error & { status?: number };
  wrapped.status = statusOf(error);
  return wrapped;
}
